const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

/** Decode entity-escaped CMS HTML (supports double-encoding). */
export function normalizeRichTextHtml(html: string): string {
  if (!html) return '';
  let normalized = html.trim();
  for (let i = 0; i < 2; i++) {
    if (!/&lt;|&gt;|&amp;(?:lt|gt|quot|#)/i.test(normalized)) break;
    normalized = normalized
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&');
  }
  return normalized;
}

/** Decode common HTML entities (supports repeated/double encoding). */
export function decodeHtmlEntities(text: string): string {
  if (!text) return '';
  let decoded = text;
  for (let i = 0; i < 3; i++) {
    const next = decoded
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .replace(/&apos;/gi, "'")
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)));
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

/** Strip HTML to plain text for previews, excerpts, and metadata. */
export function stripHtmlToPlainText(html: string): string {
  if (!html) return '';
  let text = normalizeRichTextHtml(html);
  text = text.replace(/<br\s*\/?>/gi, ' ');
  text = text.replace(/<\/p>/gi, ' ');
  text = text.replace(/<[^>]+>/g, '');
  text = decodeHtmlEntities(text);
  return text.replace(/\s+/g, ' ').trim();
}

/** Wrap legacy plain text (no HTML tags) in paragraphs. */
export function wrapPlainTextAsHtml(text: string): string {
  if (!text || HTML_TAG_PATTERN.test(text)) return text;
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return '';
  return lines.map((line) => `<p>${escapeHtml(line.trim())}</p>`).join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const ALLOWED_TAGS = new Set([
  'p',
  'div',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'h1',
  'h2',
  'h3',
  'ul',
  'ol',
  'li',
  'a',
  'span',
]);

function sanitizeHref(href: string): string | null {
  const trimmed = href.trim();
  if (!trimmed || /^javascript:/i.test(trimmed) || /^data:/i.test(trimmed)) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('/') || trimmed.startsWith('#')) {
    return trimmed;
  }
  return null;
}

function sanitizeOpeningTag(tag: string, raw: string): string {
  const lower = tag.toLowerCase();
  if (lower === 'br') return '<br>';

  if (lower === 'a') {
    const hrefMatch = raw.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
    const href = hrefMatch?.[2] ?? hrefMatch?.[3] ?? hrefMatch?.[4] ?? '';
    const safeHref = sanitizeHref(href);
    if (!safeHref) return '<a>';
    return `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer">`;
  }

  const classMatch = raw.match(/\bclass\s*=\s*("([^"]*)"|'([^']*)')/i);
  const className = classMatch?.[2] ?? classMatch?.[3];
  if (className && /^[\w\s-]+$/.test(className)) {
    return `<${lower} class="${escapeHtml(className.trim())}">`;
  }

  return `<${lower}>`;
}

/** Fix invalid block nesting from legacy div→p conversion (e.g. <p><h2>…</h2></p>). */
export function repairBlockNesting(html: string): string {
  if (!html) return '';
  return html.replace(
    /<p\b[^>]*>\s*(<(?:h[1-3]|ul|ol|div)\b[\s\S]*?<\/(?:h[1-3]|ul|ol|div)>)\s*<\/p>/gi,
    '$1',
  );
}

/** Normalize spacer divs from contenteditable; leave other blocks untouched. */
export function normalizeBlockElements(html: string): string {
  if (!html) return '';
  return html.replace(/<div\b[^>]*>\s*(<br\s*\/?>)?\s*<\/div>/gi, '<p><br></p>');
}

/** Lightweight allowlist sanitizer — no jsdom/DOMPurify (keeps Worker bundle small). */
export function sanitizeRichTextHtml(html: string): string {
  if (!html) return '';

  let result = html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<\/?(?:iframe|object|embed|form|input|button|textarea|select|svg|math)[^>]*>/gi, '');

  result = result.replace(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi, (match, tag: string) => {
    const lower = tag.toLowerCase();
    if (!ALLOWED_TAGS.has(lower)) return '';
    if (match.startsWith('</')) return `</${lower}>`;
    return sanitizeOpeningTag(lower, match);
  });

  return result.replace(/\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '');
}

/** Full pipeline: decode → plain-text wrap → sanitize → link hardening. */
export function prepareRichTextHtml(html: string): string {
  if (!html) return '';
  let result = normalizeRichTextHtml(html);
  result = wrapPlainTextAsHtml(result);
  result = normalizeBlockElements(result);
  result = repairBlockNesting(result);
  result = sanitizeRichTextHtml(result);
  result = hardenExternalLinks(result);
  return result;
}

function hardenExternalLinks(html: string): string {
  return html.replace(
    /<a\b([^>]*?)href=["'](https?:\/\/[^"']+)["']([^>]*?)>/gi,
    (_match, before: string, href: string, after: string) => {
      let attrs = `${before}href="${href}"${after}`;
      if (!/\btarget=/i.test(attrs)) attrs += ' target="_blank"';
      if (!/\brel=/i.test(attrs)) attrs += ' rel="noopener noreferrer"';
      return `<a ${attrs.trim()}>`;
    }
  );
}
